import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import {
  fetchMilestoneTemplateActionsForRuntime,
  workflowActionAllowedForAnyRole,
  normalizeWorkflowUserRoles,
} from '../../../services/workflowRuntimeService';
import { fetchWorkflowActionDefinitions } from '../../../services/workflowAdminService';
import { fetchGroup2PhasesWithMilestones } from '../../../services/productionCaseGroup2Service';
import { supabase } from '../../../lib/supabase';
import { toast } from 'sonner';
import { getOutcomeDisplay } from '../../../lib/milestoneOutcomeUtils';
import {
  assertValidMilestoneNameChoice,
  isMilestoneNameChoicePending,
  requiresMilestoneNameSelection,
} from '../../../lib/milestoneNameOptions';
import { fetchTemplateNameOptionsByMilestoneIds } from '../../../services/milestoneNameOptionsService';
import MilestoneNameChoiceField from '../../../components/milestones/MilestoneNameChoiceField';
import {
  resetPhaseMilestonesForPhaseOrder,
  resetPhaseMilestonesForRestart,
} from '../../../workflow/caseTimelinePhaseMutations';
import { completeCaseShippingPhase } from '../../../lib/caseLifecycleActions';

const LotGroup2WorkflowPanel = ({ caseId, caseNumber, currentPhase, userRole, userRoleTypes = [] }) => {
  const navigate = useNavigate();
  const effectiveRoleTypes = useMemo(() => {
    const fromProp = normalizeWorkflowUserRoles(userRoleTypes);
    if (fromProp.length) return fromProp;
    return normalizeWorkflowUserRoles(userRole);
  }, [userRole, userRoleTypes]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phases, setPhases] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [updatingMilestone, setUpdatingMilestone] = useState(null);
  const [updatingPhase, setUpdatingPhase] = useState(null);
  const [workflowDefMap, setWorkflowDefMap] = useState({});
  const [templateActionsByTemplateId, setTemplateActionsByTemplateId] = useState({});
  const [templateNameOptionsByTemplateId, setTemplateNameOptionsByTemplateId] = useState({});
  const [nameChoiceModal, setNameChoiceModal] = useState(null);
  const [pendingNameChoice, setPendingNameChoice] = useState('');
  const [completionTypeModalOpen, setCompletionTypeModalOpen] = useState(false);
  const [completionTypeMilestone, setCompletionTypeMilestone] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteMilestoneId, setNoteMilestoneId] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchPhases = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await fetchGroup2PhasesWithMilestones(caseId, currentPhase);
      setPhases(rows);
    } catch (e) {
      setLoadError(e?.message || 'Erreur de chargement');
      setPhases([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next) await fetchPhases();
  };

  useEffect(() => {
    fetchWorkflowActionDefinitions()
      .then((defs) => {
        const map = {};
        for (const d of defs) map[d.action_key] = d;
        setWorkflowDefMap(map);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!phases.length) {
      setTemplateActionsByTemplateId({});
      setTemplateNameOptionsByTemplateId({});
      return;
    }
    const ids = new Set();
    phases.forEach(p =>
      (p.milestones || []).forEach(m => {
        if (m.milestone_template_id) ids.add(m.milestone_template_id);
      })
    );
    if (!ids.size) {
      setTemplateActionsByTemplateId({});
      setTemplateNameOptionsByTemplateId({});
      return;
    }

    (async () => {
      try {
        const templateIds = [...ids];
        const [pairs, optionsMap] = await Promise.all([
          Promise.all(
            templateIds.map(async (tid) => {
              const rows = await fetchMilestoneTemplateActionsForRuntime(tid);
              rows.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              return [tid, rows];
            })
          ),
          fetchTemplateNameOptionsByMilestoneIds(templateIds),
        ]);
        setTemplateActionsByTemplateId(Object.fromEntries(pairs));
        setTemplateNameOptionsByTemplateId(optionsMap);
      } catch (e) { console.error(e); }
    })();
  }, [phases]);

  // ── Save note ─────────────────────────────────────────────────────────────
  const handleSaveNote = async () => {
    if (!noteText.trim()) { toast.warning('Veuillez entrer une note.'); return; }
    setSavingNote(true);
    try {
      const { error } = await supabase
        .from('phase_milestones')
        .update({ note: noteText.trim(), updated_at: new Date().toISOString() })
        .eq('id', noteMilestoneId);
      if (error) throw error;
      setPhases(prev =>
        prev.map(p => ({
          ...p,
          milestones: p.milestones.map(m =>
            m.id !== noteMilestoneId ? m : { ...m, note: noteText.trim() }
          ),
        }))
      );
      toast.success('Note enregistrée!');
      setNoteModalOpen(false);
      setNoteText('');
      setNoteMilestoneId(null);
    } catch (e) {
      toast.error('Erreur: ' + e.message);
    } finally {
      setSavingNote(false);
    }
  };

  // ── Phase status change ───────────────────────────────────────────────────
  const handlePhaseStatus = async (phase, newStatus) => {
    setUpdatingPhase(phase.id);
    const now = new Date().toISOString();
    const previousStatus = phase.status;
    try {
      if (newStatus === 'completed' && Number(phase.phase_order) === 6) {
        const result = await completeCaseShippingPhase(supabase, phase.id);
        if (!result?.ok) throw new Error(result?.error || 'shipping_completion_failed');
        setPhases((prev) =>
          prev.map((item) =>
            item.id === phase.id
              ? { ...item, status: 'completed', end_date: now, progress_percentage: 100 }
              : item
          )
        );
        toast.success('Commande livrée et terminée.');
        return;
      }

      const { error } = await supabase
        .from('case_timeline_phases')
        .update({
          status: newStatus,
          ...(newStatus === 'completed'
            ? { end_date: now, progress_percentage: 100 }
            : { end_date: null, progress_percentage: 0 }),
          updated_at: now,
        })
        .eq('id', phase.id);
      if (error) throw error;

      if (
        newStatus === 'in_progress' &&
        (previousStatus === 'pending' || previousStatus === 'cancelled')
      ) {
        await resetPhaseMilestonesForRestart(supabase, phase.id);
      }

      if (newStatus === 'completed') {
        const nextOrder = phase.phase_order + 1;
        const { error: nextError } = await supabase
          .from('case_timeline_phases')
          .update({ status: 'in_progress', start_date: now, updated_at: now })
          .eq('case_id', caseId)
          .eq('group', currentPhase)
          .eq('phase_order', nextOrder);
        if (nextError) console.warn('No next phase:', nextError.message);
        else await resetPhaseMilestonesForPhaseOrder(supabase, caseId, currentPhase, nextOrder);

        setPhases(prev =>
          prev.map(p => {
            if (p.id === phase.id) return { ...p, status: 'completed' };
            if (p.phase_order === phase.phase_order + 1) {
              return {
                ...p,
                status: 'in_progress',
                milestones: (p.milestones || []).map((m) => ({
                  ...m,
                  is_completed: false,
                  is_rejected: false,
                  rejected_date: null,
                  completion_type: null,
                  note: null,
                })),
              };
            }
            return p;
          })
        );
      }

      if (newStatus === 'cancelled') {
        if (phase.phase_order > 1) {
          const prevOrder = phase.phase_order - 1;
          await supabase
            .from('case_timeline_phases')
            .update({ status: 'in_progress', end_date: null, progress_percentage: 50, updated_at: now })
            .eq('case_id', caseId)
            .eq('group', currentPhase)
            .eq('phase_order', prevOrder);
        }

        setPhases(prev =>
          prev.map(p => {
            if (p.id === phase.id) return { ...p, status: 'cancelled' };
            if (phase.phase_order > 1 && p.phase_order === phase.phase_order - 1) return { ...p, status: 'in_progress' };
            return p;
          })
        );
      }

      if (
        newStatus === 'in_progress' &&
        (previousStatus === 'pending' || previousStatus === 'cancelled')
      ) {
        setPhases(prev =>
          prev.map(p =>
            p.id === phase.id
              ? {
                  ...p,
                  status: 'in_progress',
                  milestones: (p.milestones || []).map((m) => ({
                    ...m,
                    is_completed: false,
                    is_rejected: false,
                    rejected_date: null,
                    completion_type: null,
                    note: null,
                  })),
                }
              : p
          )
        );
      }

      toast.success(newStatus === 'completed' ? 'Phase confirmée' : 'Phase rejetée');
    } catch (e) {
      toast.error('Erreur: ' + e.message);
    } finally {
      setUpdatingPhase(null);
    }
  };

  // ── Milestone toggle — phase transition checked synchronously here ─────────
  // This is the key fix: we compute the updated milestone list synchronously
  // and check the transition condition immediately, instead of relying on
  // useEffect which fires after state settles and can miss or loop.
  const handleMilestoneToggle = async (milestone, phase, forceReject = false, milestoneNameOverride = null) => {
    if (phase.status !== 'in_progress') { toast.warning("Cette phase n'est pas active."); return; }
    if (!forceReject && milestone.is_completed) { toast.info('Ce jalon est déjà validé.'); return; }
    if (forceReject && milestone.is_rejected) { toast.info('Ce jalon est déjà rejeté.'); return; }

    const nameOptions = milestone.milestone_template_id
      ? templateNameOptionsByTemplateId[milestone.milestone_template_id] || []
      : [];
    if (
      !forceReject &&
      !milestoneNameOverride &&
      requiresMilestoneNameSelection(nameOptions) &&
      isMilestoneNameChoicePending(milestone, nameOptions)
    ) {
      setPendingNameChoice('');
      setNameChoiceModal({ milestone, phase, options: nameOptions, forceReject });
      return;
    }

    setUpdatingMilestone(milestone.id);
    const now = new Date().toISOString();
    const newCompleted = !forceReject;
    const newRejected = forceReject;

    try {
      const { error } = await supabase
        .from('phase_milestones')
        .update({
          is_completed: newCompleted,
          completed_date: newCompleted ? now : null,
          is_rejected: newRejected,
          rejected_date: newRejected ? now : null,
          updated_at: now,
          ...(milestoneNameOverride ? { milestone_name: milestoneNameOverride } : {}),
        })
        .eq('id', milestone.id);
      if (error) throw error;

      // Compute the updated milestone list synchronously — don't wait for state
      const updatedMilestones = (phase.milestones || []).map(m =>
        m.id !== milestone.id ? m : {
          ...m,
          is_completed: newCompleted,
          completed_date: newCompleted ? now : null,
          is_rejected: newRejected,
          rejected_date: newRejected ? now : null,
        }
      );

      // Update local state
      setPhases(prev =>
        prev.map(p =>
          p.id !== phase.id ? p : { ...p, milestones: updatedMilestones }
        )
      );

      toast.success(newCompleted ? 'Jalon validé' : 'Jalon rejeté');

      // Check transition on the computed list — reliable, no timing issues
      const allCompleted = updatedMilestones.length > 0 && updatedMilestones.every(m => m.is_completed);
      const allRejected  = updatedMilestones.length > 0 && updatedMilestones.every(m => m.is_rejected);

      if (allCompleted) {
        await handlePhaseStatus(phase, 'completed');
      } else if (allRejected) {
        await handlePhaseStatus(phase, 'cancelled');
      }

    } catch (e) {
      toast.error('Erreur: ' + e.message);
    } finally {
      setUpdatingMilestone(null);
    }
  };

  // ── Completion type ───────────────────────────────────────────────────────
  const handleSetCompletionType = async (milestoneId, type) => {
    try {
      const now = new Date().toISOString();
      const { data: { user } } = await supabase.auth.getUser();
      const row = { completion_type: type, updated_at: now };
      if (user?.id) {
        row.validated_by_user_id = user.id;
        row.validated_at = now;
      }
      const { error } = await supabase
        .from('phase_milestones')
        .update(row)
        .eq('id', milestoneId);
      if (error) throw error;
      setPhases(prev =>
        prev.map(p => ({
          ...p,
          milestones: p.milestones.map(m =>
            m.id !== milestoneId ? m : { ...m, completion_type: type }
          ),
        }))
      );
      toast.success('Type de complétion enregistré');
      setCompletionTypeModalOpen(false);
      setCompletionTypeMilestone(null);
    } catch (e) {
      toast.error('Erreur: ' + e.message);
    }
  };

  if (!caseId) return null;

  const getStatusLabel = (status) => {
    const labels = {
      draft: 'Brouillon',
      submitted: 'Soumis',
      in_review: 'En révision',
      in_progress: 'En cours',
      completed: 'Confirmé',
      cancelled: 'Annulé',
      on_hold: 'En attente',
      pending: 'En attente',
      blocked: 'Bloqué',
    };
    return labels[status] || status;
  };

  return (
    <div className="mt-4 pt-4 border-t border-border/80">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between gap-2 text-left rounded-lg px-2 py-2 -mx-2 hover:bg-muted/50 transition-colors"
        aria-expanded={open}
      >
        <span className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Icon name="Route" size={16} className="text-primary flex-shrink-0" />
          Parcours production (cas — groupe {currentPhase})
        </span>
        <Icon name={open ? 'ChevronUp' : 'ChevronDown'} size={18} className="text-muted-foreground flex-shrink-0" />
      </button>

      {open && (
        <div className="mt-3 space-y-3 pl-1">
          {loading && (
            <p className="text-xs text-muted-foreground flex items-center gap-2">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              Chargement du parcours…
            </p>
          )}
          {loadError && !loading && (
            <div className="flex flex-col gap-2">
              <p className="text-xs text-destructive">{loadError}</p>
              <button type="button" className="text-xs text-primary hover:underline w-fit" onClick={fetchPhases}>
                Réessayer
              </button>
            </div>
          )}
          {!loading && !loadError && phases.length === 0 && (
            <p className="text-xs text-muted-foreground">
              Aucune phase de production (groupe {currentPhase}) pour ce cas. Gérez le parcours sur la fiche cas.
            </p>
          )}

          {!loading &&
            phases
              .filter((p) => {
                const groupNum = parseInt(currentPhase, 10);
                const phaseOrder = parseInt(p.phase_order, 10);
                if (isNaN(groupNum) || isNaN(phaseOrder)) return false;
                if (groupNum === 1) return [5, 6, 7].includes(phaseOrder);
                if (groupNum > 1) return [2, 3, 4].includes(phaseOrder);
                return false;
              })
              .map((phase) => (
                <div key={phase.id} className="rounded-lg border border-border bg-muted/20 px-3 py-2.5">
                  {/* Phase header */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-primary tabular-nums w-6">
                      {phase.phase_order}
                    </span>
                    <span className="text-sm font-medium text-foreground">{phase.phase_name}</span>
                    {phase.status && (
                      <span className={`text-[10px] uppercase tracking-wide font-semibold ml-auto ${
                        phase.status === 'completed'   ? 'text-success' :
                        phase.status === 'in_progress' ? 'text-warning' :
                        phase.status === 'cancelled'   ? 'text-destructive' :
                        'text-muted-foreground'
                      }`}>
                        {getStatusLabel(phase.status)}
                      </span>
                    )}
                  </div>

                  {/* Milestones */}
                  <ul className="space-y-1.5 pl-7 border-l border-border/60 ml-1">
                    {(phase.milestones || []).map((m) => (
                      <li key={m.id} className="flex flex-col gap-1.5 text-xs py-1">
                        <div className="flex items-start gap-2">
                          <Icon
                            name={m.is_completed ? 'CheckCircle2' : m.is_rejected ? 'XCircle' : 'Circle'}
                            size={14}
                            className={
                              m.is_completed ? 'text-green-600 dark:text-green-400' :
                              m.is_rejected  ? 'text-red-600 dark:text-red-400' :
                              'text-foreground'
                            }
                          />
                          <span className={
                            m.is_completed ? 'text-green-600 dark:text-green-400 font-semibold' :
                            m.is_rejected  ? 'text-red-600 dark:text-red-400' :
                            'text-foreground'
                          }>
                            {m.milestone_name}

                            {(() => {
                              const outcome = getOutcomeDisplay(m.completion_type);
                              if (!outcome) return null;
                              return (
                                <span className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${outcome.badgeClass}`}>
                                  <Icon name={outcome.icon} size={10} />
                                  {outcome.label}
                                </span>
                              );
                            })()}

                            <span className={`text-[9px] font-bold uppercase ${
                              m.is_completed ? 'text-green-500 dark:text-green-400' :
                              m.is_rejected  ? 'text-red-500 dark:text-red-400' :
                              'text-muted-foreground'
                            }`}>
                              {m.is_completed && m.completed_date
                                ? ` : Validé le ${new Date(m.completed_date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                : m.is_rejected && m.rejected_date
                                ? ` : Rejeté le ${new Date(m.rejected_date).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                                : ' : En attente de validation'
                              }
                            </span>

                            {m.note && (
                              <div className="ml-5 mt-1 flex items-start gap-1.5 text-[10px] text-muted-foreground bg-muted/40 border border-border rounded px-2 py-1">
                                <Icon name="StickyNote" size={11} className="flex-shrink-0 mt-0.5" />
                                <span className="whitespace-pre-wrap">{m.note}</span>
                              </div>
                            )}
                          </span>
                        </div>

                        {/* Action buttons */}
                        {(() => {
                          const templateId = m.milestone_template_id;
                          const allActions = templateId ? (templateActionsByTemplateId[templateId] || []) : [];
                          const allowedActions = allActions.filter(row =>
                            workflowActionAllowedForAnyRole(effectiveRoleTypes, row.allowed_role_types)
                          );

                          // No template actions — show plain buttons
                          if (!allowedActions.length) {
                            if (phase.status !== 'in_progress') return null;
                            if (m.is_completed || m.is_rejected) return null;
                            return (
                              <div className="flex gap-1 flex-shrink-0">
                                <button
                                  disabled={updatingMilestone === m.id}
                                  onClick={() => handleMilestoneToggle(m, phase)}
                                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-green-400/60 text-green-600 bg-green-400/10 hover:bg-green-400/20 text-[10px] font-medium transition-all disabled:opacity-50"
                                >
                                  Confirmer
                                </button>
                                <button
                                  disabled={updatingMilestone === m.id}
                                  onClick={() => handleMilestoneToggle(m, phase, true)}
                                  className="flex items-center gap-1 px-2 py-0.5 rounded border border-red-400/60 text-red-600 bg-red-400/10 hover:bg-red-400/20 text-[10px] font-medium transition-all disabled:opacity-50"
                                >
                                  Rejeter
                                </button>
                              </div>
                            );
                          }

                          return (
                            <div className="flex gap-1 flex-shrink-0 flex-wrap">
                              {allowedActions.map(row => {
                                const def = workflowDefMap[row.action_key];
                                const label = def?.label || row.action_key;
                                const iconName = def?.icon || null;

                                if (row.action_key === 'confirm_milestone') {
                                  if (phase.status !== 'in_progress') return null;
                                  if (m.is_completed) return null;
                                  return (
                                    <button
                                      key={row.action_key}
                                      disabled={updatingMilestone === m.id}
                                      onClick={() => handleMilestoneToggle(m, phase)}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-green-400/60 text-green-600 bg-green-400/10 hover:bg-green-400/20 text-[10px] font-medium transition-all disabled:opacity-50"
                                    >
                                      {iconName && <Icon name={iconName} size={11} />}
                                      {label}
                                    </button>
                                  );
                                }

                                if (row.action_key === 'reject_milestone') {
                                  if (phase.status !== 'in_progress') return null;
                                  if (m.is_rejected) return null;
                                  return (
                                    <button
                                      key={row.action_key}
                                      disabled={updatingMilestone === m.id}
                                      onClick={() => handleMilestoneToggle(m, phase, true)}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-red-400/60 text-red-600 bg-red-400/10 hover:bg-red-400/20 text-[10px] font-medium transition-all disabled:opacity-50"
                                    >
                                      {iconName && <Icon name={iconName} size={11} />}
                                      {label}
                                    </button>
                                  );
                                }

                                if (row.action_key === 'set_completion_type') {
                                  if (phase.status !== 'in_progress') return null;
                                  return (
                                    <button
                                      key={row.action_key}
                                      onClick={() => { setCompletionTypeMilestone(m); setCompletionTypeModalOpen(true); }}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-purple-400/60 text-purple-600 bg-purple-400/10 hover:bg-purple-400/20 text-[10px] font-medium transition-all"
                                      title={def?.description || label}
                                    >
                                      {iconName && <Icon name={iconName} size={11} />}
                                      {label}
                                    </button>
                                  );
                                }

                                if (row.action_key === 'add_milestone_note') {
                                  if (phase.status !== 'in_progress') return null;
                                  return (
                                    <button
                                      key={row.action_key}
                                      onClick={() => { setNoteMilestoneId(m.id); setNoteText(m.note ?? ''); setNoteModalOpen(true); }}
                                      className="flex items-center gap-1 px-2 py-0.5 rounded border border-orange-400/60 text-orange-600 bg-orange-400/10 hover:bg-orange-400/20 text-[10px] font-medium transition-all"
                                      title={def?.description || label}
                                    >
                                      {iconName && <Icon name={iconName} size={11} />}
                                      {label}
                                    </button>
                                  );
                                }

                                if (row.action_key === 'confirm_phase' || row.action_key === 'reject_phase') return null;
                                if (phase.status !== 'in_progress') return null;

                                return (
                                  <button
                                    key={row.action_key}
                                    className="flex items-center gap-1 px-2 py-0.5 rounded border border-border text-muted-foreground bg-muted/30 text-[10px] font-medium"
                                    title={def?.description || label}
                                  >
                                    {iconName && <Icon name={iconName} size={11} />}
                                    {label}
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            iconPosition="left"
            fullWidth
            className="min-touch-target mt-2"
            onClick={() => navigate(`/case-detail/${caseId}`)}
          >
            Gérer sur la fiche cas
            {caseNumber ? ` (${caseNumber})` : ''}
          </Button>
        </div>
      )}

      {/* ── Note modal ──────────────────────────────────────────────────────── */}
      {noteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="StickyNote" size={18} className="text-orange-400" />
              <h3 className="text-base font-semibold text-foreground">Ajouter une note</h3>
            </div>
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Saisissez votre note ici..."
              rows={5}
              className="w-full bg-muted/50 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground outline-none resize-none focus:border-primary transition-colors"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => { setNoteModalOpen(false); setNoteText(''); setNoteMilestoneId(null); }}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleSaveNote}
                disabled={savingNote}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 active:scale-95 disabled:opacity-50 transition-all"
              >
                {savingNote
                  ? <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  : <Icon name="Save" size={14} />
                }
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}

      {nameChoiceModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-semibold text-foreground mb-4">Choisir une valeur</h3>
            <MilestoneNameChoiceField
              options={nameChoiceModal.options}
              value={pendingNameChoice}
              onChange={setPendingNameChoice}
            />
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => {
                  setNameChoiceModal(null);
                  setPendingNameChoice('');
                }}
                className="flex-1 px-4 py-2 text-sm rounded-lg border border-border"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={!pendingNameChoice}
                onClick={() => {
                  try {
                    const name = assertValidMilestoneNameChoice(
                      pendingNameChoice,
                      nameChoiceModal.options
                    );
                    const { milestone, phase, forceReject } = nameChoiceModal;
                    setNameChoiceModal(null);
                    setPendingNameChoice('');
                    handleMilestoneToggle(milestone, phase, forceReject, name);
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
                className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-primary-foreground disabled:opacity-50"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ── Completion type modal ────────────────────────────────────────────── */}
      {completionTypeModalOpen && completionTypeMilestone && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-background border border-border rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="ToggleLeft" size={18} className="text-purple-400" />
              <h3 className="text-base font-semibold text-foreground">Type de complétion</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-5">
              Sélectionnez le type de complétion pour{' '}
              <span className="font-medium text-foreground">{completionTypeMilestone.milestone_name}</span>.
              {completionTypeMilestone.completion_type === 'partiel' && (
                <span className="block mt-2 text-xs text-amber-500 font-medium">
                  ⚠️ Un jalon partiel ne peut pas être validé.
                </span>
              )}
            </p>
            <div className="flex gap-2 mb-5">
              {[
                { type: 'complet', label: 'Complète', icon: 'CheckCircle2', active: 'border-green-500 bg-green-500/10 text-green-600', hover: 'hover:border-green-400 hover:bg-green-400/5' },
                { type: 'partiel', label: 'Partiel', icon: 'CircleDashed', active: 'border-amber-500 bg-amber-500/10 text-amber-600', hover: 'hover:border-amber-400 hover:bg-amber-400/5' },
                { type: 'echec', label: 'Échec', icon: 'XCircle', active: 'border-red-500 bg-red-500/10 text-red-600', hover: 'hover:border-red-400 hover:bg-red-400/5' },
              ].map((opt) => (
                <button
                  key={opt.type}
                  type="button"
                  onClick={() => handleSetCompletionType(completionTypeMilestone.id, opt.type)}
                  className={`flex-1 flex flex-col items-center gap-2 px-3 py-3 rounded-lg border-2 transition-all ${
                    completionTypeMilestone.completion_type === opt.type
                      ? opt.active
                      : `border-border text-muted-foreground ${opt.hover}`
                  }`}
                >
                  <Icon name={opt.icon} size={22} />
                  <span className="text-sm font-medium">{opt.label}</span>
                </button>
              ))}
            </div>
            <button
              onClick={() => { setCompletionTypeModalOpen(false); setCompletionTypeMilestone(null); }}
              className="w-full px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:bg-muted transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LotGroup2WorkflowPanel;